import { Dispatch, useCallback } from 'react'
import { TerminalEventMonitoring } from '@lightspeed-payments/payments-terminal-sdk'
import { useErrorMessages } from '../../../../hooks/useErrorMessages'
import {
  LSPAY_REFUND_TRANSACTION_STATUS,
  TRANSACTION_TYPE,
} from '../../../../helpers/lspayConstants'
import { sendTerminalEvent } from '../../../../helpers/terminalMonitoringEventDispatcher'
import {
  getCancelledError,
  getRefundDeclinedError,
  isCardSpecificDecline,
} from '../../../../helpers/errorChecking'
import { RefundResponse, TerminalInfo } from '../../../../helpers/lspay.types'
import { RefundEvent } from './use-lspay-refund-server-reducer/types'

type DispatchType = {
  skipCompletedEvent?: boolean
  dispatch: Dispatch<RefundEvent>
  data: RefundResponse
  terminalMonitoringInstance: TerminalEventMonitoring | null
  setupIntentId: string
  terminalInfo: TerminalInfo
}
export const useDispatchRefundEvent = () => {
  const getErrorMessage = useErrorMessages()

  const dispatchEvent = useCallback(
    ({
      skipCompletedEvent,
      dispatch,
      data,
      terminalMonitoringInstance,
      setupIntentId,
      terminalInfo,
    }: DispatchType) => {
      const { deviceId, deviceModel } = terminalInfo

      if (data.status === LSPAY_REFUND_TRANSACTION_STATUS.PROCESSING) {
        dispatch({
          type: 'START_POLLING',
          setupIntentId,
        })
      }
      if (data.status === LSPAY_REFUND_TRANSACTION_STATUS.CANCELLED) {
        // Send terminal monitoring events for cancelled refund
        if (!data.reason) {
          // Send terminal monitoring event with cancelled type and reason
          // since it likely the refund was cancelled by user by the cancel button
          sendTerminalEvent({
            terminalMonitoringInstance,
            intentId: setupIntentId,
            terminal: {
              id: deviceId,
              model: deviceModel,
            },
            reason: 'Requested by user',
            status: LSPAY_REFUND_TRANSACTION_STATUS.CANCELLED,
            transactionType: TRANSACTION_TYPE.REFUND,
          })
        } else {
          // Send the terminal monitoring event with error type for the cancelled refund with reason
          // since it likey the refund was cancelled by swiper due to some error
          sendTerminalEvent({
            terminalMonitoringInstance,
            intentId: setupIntentId,
            terminal: {
              id: terminalInfo?.deviceId,
              model: terminalInfo?.deviceModel,
            },
            reason: data.reason,
            transactionType: TRANSACTION_TYPE.REFUND,
          })
        }

        dispatch({
          type: 'CANCELLED',
          setupIntentId,
          errorMessage: getErrorMessage(getCancelledError(data.reason, true)),
          reason: data.reason,
          reprocessingAvailable: data.reprocessing_available,
        })
        return
      }
      if (data.status === LSPAY_REFUND_TRANSACTION_STATUS.DECLINED) {
        // Send terminal monitoring event for declined refund
        sendTerminalEvent({
          terminalMonitoringInstance,
          intentId: setupIntentId,
          status: LSPAY_REFUND_TRANSACTION_STATUS.DECLINED,
          reason: data.reason,
          terminal: {
            id: deviceId,
            model: deviceModel,
          },
          transactionType: TRANSACTION_TYPE.REFUND,
        })

        dispatch({
          type: 'DECLINED',
          setupIntentId,
          errorMessage: getErrorMessage(
            getRefundDeclinedError(data.reason),
            isCardSpecificDecline(data.reason)
          ),
          reason: data.reason,
          reprocessingAvailable: data.reprocessing_available,
        })
        return
      }
      if (data.status === LSPAY_REFUND_TRANSACTION_STATUS.CARD_PRESENTED) {
        dispatch({
          type: 'CARD_PRESENTED',
          setupIntentId,
        })
        return
      }
      if (
        data.status === LSPAY_REFUND_TRANSACTION_STATUS.COMPLETED &&
        data.transaction
      ) {
        !skipCompletedEvent &&
          sendTerminalEvent({
            terminalMonitoringInstance,
            intentId: setupIntentId,
            terminal: {
              id: deviceId,
              model: deviceModel,
            },
            status: LSPAY_REFUND_TRANSACTION_STATUS.COMPLETED,
            transactionType: TRANSACTION_TYPE.REFUND,
          })
        dispatch({
          type: 'COMPLETE_REFUND',
          transaction: data.transaction,
        })
      }
    },
    [getErrorMessage]
  )

  return { dispatchEvent, getErrorMessage }
}
